import { fieldRegistry } from '../../../core/fields/registry/FieldRegistry';
import { getFieldSection } from './field-sections.config';
import type { FieldManifest } from '../../../core/fields/types';
import { FieldType } from '../../../core/fields/types';

function mapFieldTypeToKind(fieldType: FieldType): string {
  switch (fieldType) {
    case FieldType.TEXT:
      return 'string';
    case FieldType.NUMBER:
      return 'number';
    case FieldType.BOOLEAN:
      return 'boolean';
    case FieldType.SELECT:
      return 'enum';
    case FieldType.MULTI_SELECT:
      return 'tags';
    case FieldType.DATE:
      return 'date';
    case FieldType.RICH_TEXT:
      return 'richtext';
    case FieldType.COMPOSITE:
      return 'composite';
    default:
      return 'string';
  }
}

const SECTION_TO_GROUP_MAP: Record<string, string> = {
  identity: 'identity',
  role: 'role',
  organization: 'organization',
  talent: 'talent',
  succession: 'succession',
  compensation: 'compensation',
  skills: 'skills',
  workforce: 'workforce',
  demographics: 'demographics',
  engagement: 'engagement',
  notes: 'extras',
};

function determineControl(field: FieldManifest): string {
  if (field.type === FieldType.SELECT) {
    if (field.options && field.options.length <= 3) {
      return 'toggle-group';
    }
    return 'select';
  }
  if (field.type === FieldType.BOOLEAN) return 'switch';
  if (field.type === FieldType.MULTI_SELECT) return 'tags';
  if (field.type === FieldType.RICH_TEXT || field.multiline) return 'textarea';
  if (field.type === FieldType.DATE) return 'date';
  return 'text';
}

interface ManifestProperty {
  key: string;
  label: string;
  kind: string;
  required?: boolean;
  namespace?: string;
  enumValues?: string[];
  options?: { value: string; label: string; color?: string; icon?: string }[];
  allowCustomOptions?: boolean;
  ui: {
    group: string;
    priority: number;
    width: string;
    control: string;
    placeholder?: string;
    collapsedByDefault?: boolean;
  };
}

export function generatePropertiesFromFieldPacks(fieldPackIds: string[]): ManifestProperty[] {
  const fields = fieldRegistry.getFieldsFromPacks(fieldPackIds);
  const properties: ManifestProperty[] = [];

  for (const field of fields) {
    const tags = Array.isArray(field.tags)
      ? field.tags.map(tag => typeof tag === 'string' ? tag : tag.id)
      : [];

    const section = getFieldSection(tags);
    if (!section) continue;

    const group = SECTION_TO_GROUP_MAP[section.id] || 'extras';
    const kind = mapFieldTypeToKind(field.type);
    const control = determineControl(field);

    const property: ManifestProperty = {
      key: field.id,
      label: field.label,
      kind,
      ui: {
        group,
        priority: field.priority || 50,
        width: 'full',
        control,
      },
    };

    if (field.required) property.required = true;

    if (field.type === FieldType.SELECT && field.options) {
      property.enumValues = field.options.map(opt => opt.value);
      property.options = field.options;
    }

    if (field.type === FieldType.MULTI_SELECT) {
      if (field.options) property.options = field.options;
      if (field.allowCustomOptions) property.allowCustomOptions = true;
    }

    if (field.placeholder) property.ui.placeholder = field.placeholder;
    if (field.namespace) property.namespace = field.namespace;
    if (field.priority && field.priority < 50) property.ui.collapsedByDefault = true;

    properties.push(property);
  }

  const groupOrder = Object.values(SECTION_TO_GROUP_MAP);
  properties.sort((a, b) => {
    const groupA = groupOrder.indexOf(a.ui.group);
    const groupB = groupOrder.indexOf(b.ui.group);
    if (groupA !== groupB) return groupA - groupB;
    return (b.ui.priority || 0) - (a.ui.priority || 0);
  });

  return properties;
}

export function getOrgChartPersonFieldPacks(): string[] {
  return [
    'orgchart-base',
    'orgchart-organization',
    'orgchart-role',
    'orgchart-talent',
    'orgchart-compensation',
    'orgchart-skills',
    'orgchart-workforce',
    'orgchart-succession',
    'orgchart-demographics',
    'orgchart-engagement',
  ];
}

export function getOrgChartDepartmentFieldPacks(): string[] {
  return ['orgchart-department'];
}

export function getOrgChartPositionFieldPacks(): string[] {
  return ['orgchart-position'];
}

export function getOrgChartTeamFieldPacks(): string[] {
  return ['orgchart-team'];
}

export function getOrgChartProjectFieldPacks(): string[] {
  return ['orgchart-project'];
}

export function getOrgChartRoleProfileFieldPacks(): string[] {
  return ['orgchart-role-profile'];
}
